import { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import FallbackLoading from "./components/loader/FallbackLoading";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./containers/Layout";
import routes, { privateRoutes } from "./routes";
import i18next from "./i18n/i18n";
import ServerIP from "./utils/serverIp.jsx";
// import websocketService from "./service/websocketService";
import mqttService from "./service/mqttService";
import SignIn from "./pages/SignIn";
import SiteSelector from "./pages/SiteSelector";
import { isSiteSelectionRequired } from "./utils/siteSelection";

const languageMap = {
  English: "en",
  French: "fr",
  Arabic: "ar",
};
const App = () => {
  //const userData = localStorage.getItem("user");
  const userData = useSelector((state) => state.auth?.userData);
  const activeSite = useSelector((state) => state.auth?.site);
  const selectionRequired = isSiteSelectionRequired();
  const location = useLocation();
  
  useEffect(() => {
    // Connect to services when the user is logged in
    if (userData?.user_ID) {
      // websocketService.connect(userData.user_ID);
      mqttService.connect(userData.user_ID);
    }

    // Disconnect when the component unmounts or the user logs out
    return () => {
      // websocketService.close();
      mqttService.close();
    };
  }, [userData]);

  useEffect(() => {
    const handleFirstInteraction = () => {
      mqttService.unlockAudio();
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };

    window.addEventListener('click', handleFirstInteraction);
    window.addEventListener('keydown', handleFirstInteraction);

    return () => {
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('keydown', handleFirstInteraction);
    };
  }, []);

  useEffect(() => {
    if (userData?.Language) {
      const userLanguage = languageMap[userData.Language] || "en";
      i18next.changeLanguage(userLanguage);
    }
  }, [userData]);
  
  return (
    // Last-resort net: catches anything that escapes a route's own boundary
    // (e.g. Layout, SignIn, or SiteSelector itself throwing). Keyed by
    // pathname so navigating away from a crashed route clears the error.
    <ErrorBoundary resetKey={location.pathname} fallback={<RootErrorFallback />}>
    <Suspense fallback={<FallbackLoading />}>
      <>
        {!userData && <ServerIP />}
        {/* {error && <ErrorMessage message={error.status} />}
        <h1 className="side_bar_bg">solyntek</h1>*/}
        <Routes>
          {routes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          <Route
            path="/"
            element={
              userData
                ? selectionRequired && !activeSite
                  ? <Navigate to="/select-site" replace />
                  : <Layout />
                : <Navigate to="/signin" replace />
            }
          >
            {/* <Route path={'/'} element={<div>dd</div>} /> */}
            {privateRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ))}
          </Route>

          <Route
            path="/signin"
            element={
              !userData
                ? <SignIn />
                : <Navigate to={selectionRequired && !activeSite ? "/select-site" : "/"} replace />
            }
          />
          <Route
            path="/select-site"
            element={
              userData
                ? selectionRequired && !activeSite
                  ? <SiteSelector />
                  : <Navigate to="/" replace />
                : <Navigate to="/signin" replace />
            }
          />
        </Routes>
      </>
    </Suspense>
    </ErrorBoundary>
  );
};

function RootErrorFallback() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-white p-10 text-center font-roboto">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
        <svg className="h-8 w-8 text-red-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" />
          <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          <path d="M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <div>
        <p className="text-lg font-bold text-gray-700">Something went wrong</p>
        <p className="mt-1 text-sm text-gray-500">The app ran into an unexpected error. Reloading usually fixes it.</p>
      </div>
      <div className="flex gap-3">
        <button
          onClick={() => window.location.reload()}
          className="rounded-2xl bg-[#0071BC] px-4 py-2 text-sm font-bold text-white hover:opacity-80"
        >
          Reload
        </button>
        <button
          onClick={() => { window.location.href = "/signin"; }}
          className="rounded-2xl bg-white px-4 py-2 text-sm font-bold text-gray-700 border border-gray-200 hover:opacity-80"
        >
          Go to sign in
        </button>
      </div>
    </div>
  );
}

export default App;
